import React, { FC, useEffect } from 'react';
import Modal from 'components/shared/Modal';
import { usePostCommunicationContext } from './PostCommunicationProvider';
import PostCommunicationTabBar from './PostCommunicationTabBar';
import { PostCommunicationContent } from './types/postCommunicationContent';
import ActivityLog from './ActivityLog';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import CommentsTextEditor from './comments/CommentsTextEditor';
import AccordionWidgetIcon from 'app/modules/posts/components/accordionWidget/AccordionWidgetIcon';
import { WIDGET_VARIANT_PROPERTIES } from 'app/modules/posts/components/accordionWidget/constants/widgetVariant';
import CommentsThreads from 'app/modules/posts/modules/post/components/postCommunication/comments/CommentsThreads';
import PostVersions from './PostRevisions';
import { useTranslation } from 'react-i18next';

type Props = {
  onClose: () => void;
  isOpen: boolean;
};

const PostCommunicationFullScreenModal: FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const {
    activeContent,
    changeActiveContent,
    postId,
    setReadyToTrackVisibility,
  } = usePostCommunicationContext();

  const communicationWidget = WIDGET_VARIANT_PROPERTIES['communication'];

  useEffect(() => {
    setReadyToTrackVisibility(isOpen);
  }, [isOpen, setReadyToTrackVisibility]);

  return (
    <Modal
      open={isOpen}
      classNames={{
        modal: '!tw-h-full !tw-m-0',
        modalContainer: '!tw-h-full !tw-overflow-hidden',
        overlay: '!tw-bg-grayscale-5 !tw-bg-opacity-1',
      }}
      focusTrapped={false}
      size="responsive"
      isContentHeightAutoResizable={false}
      dataCy={{
        wrapper: 'post-communication-fullscreen',
      }}
      onClose={onClose}
    >
      <div className="tw-h-full tw-p-4">
        <div className="tw-flex tw-h-full tw-flex-col">
          <div
            className="tw-flex tw-h-full tw-flex-col tw-overflow-y-auto"
            data-name="communication_expanded-wrapper"
          >
            <Modal.Header
              className="tw-p-0 tw-pb-2"
              title={
                <div className="tw-flex tw-items-center">
                  <AccordionWidgetIcon variant="communication" />
                  <span className="tw-ml-2 tw-mr-1 tw-text-base">
                    {t(communicationWidget.title)}
                  </span>
                </div>
              }
            >
              <PostCommunicationTabBar
                activeTab={activeContent}
                onActiveTabChange={changeActiveContent}
              />
            </Modal.Header>
            <Modal.Content className="tw-h-full tw-max-h-[100%] tw-overflow-y-auto tw-p-0">
              {[
                PostCommunicationContent.INTERNAL,
                PostCommunicationContent.CLIENT,
                PostCommunicationContent.PINNED,
              ].includes(activeContent) && (
                <CommentsThreads className="tw-h-full tw-max-h-[100%] tw-overflow-auto" />
              )}
              {activeContent === PostCommunicationContent.ACTIVITY && (
                <ActivityLog postId={postId} />
              )}
              {activeContent === PostCommunicationContent.VERSIONS && (
                <PostVersions postId={postId} />
              )}
            </Modal.Content>

            <div className="tw-py-2">
              {[
                PostCommunicationContent.INTERNAL,
                PostCommunicationContent.CLIENT,
              ].includes(activeContent) && (
                <UserPermissionGate
                  scopes={
                    activeContent === PostCommunicationContent.INTERNAL
                      ? UserPermission.POST_COMMUNICATION_INTERNAL_CREATE
                      : UserPermission.POST_COMMUNICATION_CLIENT_CREATE
                  }
                >
                  <CommentsTextEditor />
                </UserPermissionGate>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PostCommunicationFullScreenModal;
